import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, UserCog, XCircle, FileText } from 'lucide-react'
import {
  listPendingApprovals,
  confirmApproval,
  takeoverApproval,
  rejectApproval,
  type PendingApprovalItem,
} from '@/api/zuzahlerApprovals'
import Spinner from '@/components/Spinner'
import ErrorAlert from '@/components/ErrorAlert'

/**
 * Phase G — Inbox des Firma-Admins fuer Case-2-Approvals.
 *
 * Route: /firma/:firmaId/approvals  (firmaId = CRM accountnumber = datapool.t_firmen.FirmenID)
 * Auth:  BFF prueft is_firma_admin(firmaId) auf jedem Request.
 *
 * Pro Eintrag drei Aktionen:
 *   - Confirm:  Neuen AP bestaetigen → Onboarding-Mail an den AP
 *   - Takeover: Admin macht es selbst → KU geht in seine eigene Aufgaben-Inbox (Phase H)
 *   - Reject:   Firma uebernimmt nicht → AP + Buchhaltung werden informiert
 */
export default function ZuzahlerApprovalsPage() {
  const { firmaId: firmaIdParam } = useParams<{ firmaId: string }>()
  const firmaId = Number(firmaIdParam)

  if (!firmaId || Number.isNaN(firmaId)) {
    return (
      <div className="px-6 py-6 max-w-7xl mx-auto">
        <ErrorAlert message="Ungueltige Firma-ID in der URL." />
      </div>
    )
  }

  return <ApprovalsList firmaId={firmaId} />
}

function ApprovalsList({ firmaId }: { firmaId: number }) {
  const qc = useQueryClient()
  const [rejectFor, setRejectFor] = useState<PendingApprovalItem | null>(null)

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['zuzahlerApprovals', firmaId] })

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['zuzahlerApprovals', firmaId],
    queryFn: () => listPendingApprovals(firmaId),
  })

  const confirmMut = useMutation({
    mutationFn: (id: number) => confirmApproval(firmaId, id),
    onSuccess: invalidate,
  })
  const takeoverMut = useMutation({
    mutationFn: (id: number) => takeoverApproval(firmaId, id),
    onSuccess: invalidate,
  })
  const rejectMut = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      rejectApproval(firmaId, id, reason),
    onSuccess: () => {
      setRejectFor(null)
      invalidate()
    },
  })

  const items = data?.items ?? []

  return (
    <div className="px-6 py-6 max-w-6xl mx-auto">
      <div className="bg-white border border-gray-200 rounded-lg px-6 py-5 mb-5">
        <h1 className="text-[1.35rem] font-bold text-gray-900 tracking-tight flex items-center gap-2">
          <FileText size={20} className="text-fom-500" />
          Zuzahler-Bestaetigungen
        </h1>
        <p className="text-gray-500 text-[0.78rem] mt-0.5">
          Neue Ansprechpartner fuer Kostenuebernahmen Ihrer Firma — bitte pruefen und entscheiden.
        </p>
      </div>

      {isLoading && <Spinner className="mt-10" />}
      {error && <ErrorAlert message={(error as Error).message} onRetry={refetch} />}

      {!isLoading && !error && items.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-500 text-sm">
          Aktuell sind keine Bestaetigungen offen.
        </div>
      )}

      {!isLoading && !error && items.length > 0 && (
        <div className="space-y-4">
          {items.map((item) => (
            <ApprovalCard
              key={item.Id}
              item={item}
              busy={
                confirmMut.isPending ||
                takeoverMut.isPending ||
                rejectMut.isPending
              }
              onConfirm={() => confirmMut.mutate(item.Id)}
              onTakeover={() => takeoverMut.mutate(item.Id)}
              onReject={() => setRejectFor(item)}
            />
          ))}
        </div>
      )}

      {rejectFor && (
        <RejectModal
          item={rejectFor}
          onCancel={() => setRejectFor(null)}
          onSubmit={(reason) =>
            rejectMut.mutate({ id: rejectFor.Id, reason })
          }
          busy={rejectMut.isPending}
        />
      )}
    </div>
  )
}

function ApprovalCard({
  item,
  busy,
  onConfirm,
  onTakeover,
  onReject,
}: {
  item: PendingApprovalItem
  busy: boolean
  onConfirm: () => void
  onTakeover: () => void
  onReject: () => void
}) {
  const studiName = [item.StudiAnrede, item.StudiVorname, item.StudiNachname]
    .filter(Boolean)
    .join(' ')
  const requestedAt = item.AdminApprovalRequestedAt
    ? new Date(item.AdminApprovalRequestedAt).toLocaleDateString('de-DE')
    : null

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5">
      <div className="flex justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="font-semibold text-gray-900">{studiName || '–'}</div>
          <div className="text-xs text-gray-500 mt-0.5">
            Matrikel: <span className="font-mono">{item.StudiMatrikel ?? '–'}</span>
            {item.StudiStudienort && (
              <span className="text-gray-400"> · Studienort {item.StudiStudienort}</span>
            )}
          </div>
        </div>
        {requestedAt && (
          <div className="text-xs text-gray-400">Anfrage vom {requestedAt}</div>
        )}
      </div>

      <div className="bg-gray-50 border border-gray-100 rounded-md p-3 mb-4">
        <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">
          Neuer Ansprechpartner
        </div>
        <div className="text-sm font-medium text-gray-900">
          {item.ZuzahlerApName ?? '–'}
        </div>
        {item.ZuzahlerApEmail && (
          <div className="text-xs text-gray-500 mt-0.5">{item.ZuzahlerApEmail}</div>
        )}
        {item.ZuzahlerFirma && (
          <div className="text-xs text-gray-400 mt-0.5">{item.ZuzahlerFirma}</div>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={onConfirm}
          disabled={busy}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <CheckCircle2 size={14} /> Neuen AP bestaetigen
        </button>
        <button
          onClick={onTakeover}
          disabled={busy}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <UserCog size={14} /> Ich mache es selbst
        </button>
        <button
          onClick={onReject}
          disabled={busy}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-rose-200 text-rose-700 bg-rose-50 hover:bg-rose-100 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <XCircle size={14} /> Uebernehmen wir nicht
        </button>
      </div>
    </div>
  )
}

function RejectModal({
  item,
  onCancel,
  onSubmit,
  busy,
}: {
  item: PendingApprovalItem
  onCancel: () => void
  onSubmit: (reason: string) => void
  busy: boolean
}) {
  const [reason, setReason] = useState('')
  const studiName = [item.StudiVorname, item.StudiNachname].filter(Boolean).join(' ')

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-2">
          Kosten nicht uebernehmen
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Sie sind dabei zu bestaetigen, dass Ihre Firma die Kosten fuer
          <b> {studiName || 'die/den Studierende/n'} </b>
          nicht uebernimmt. Sowohl der angegebene Ansprechpartner als auch die FOM-Buchhaltung
          werden per Mail informiert.
        </p>
        <label className="block text-xs font-semibold text-gray-700 mb-1">
          Begruendung (optional, wird in der Mail an den AP zitiert)
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="z.B. Mitarbeiter nicht mehr in unserer Firma taetig"
          className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-fom-500 outline-none"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-2 text-xs font-semibold rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60"
          >
            Abbrechen
          </button>
          <button
            onClick={() => onSubmit(reason)}
            disabled={busy}
            className="px-3 py-2 text-xs font-semibold rounded-lg border border-rose-300 text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-60"
          >
            Ablehnen
          </button>
        </div>
      </div>
    </div>
  )
}
